// Admin SAT Talep listesi (5 asamali akis).
// Tum talepler bir kez yuklenir; sekmeler/filtreler istemcide calisir ve sayaclar gosterilir:
//   - "Sirasi bende": rolun onayini bekleyen talepler (birim amiri: kendi astlari,
//     IT: pending_it, yonetici: pending_manager, IK: pending_hr)
//   - Onay bekleyen / Tamamlanan / Reddedilen-Iptal / Tumu
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../providers/I18nProvider";
import { useAuth } from "../contexts/AuthContext";
import { hasAnyRole } from "../utils/roles";
import { satRequestService } from "../services/satRequestService";
import StatusBadge from "../components/satRequest/StatusBadge";
import PriorityBadge from "../components/satRequest/PriorityBadge";
import SatFilterBar from "../components/satRequest/SatFilterBar";
import {
  isClosedStatus,
  isPendingStatus,
  requestTotal,
  useSatListFilters,
  waitingDays,
} from "../components/satRequest/satListUtils";
import { TabBar, EmptyState, countBy } from "../components/ListToolbar";
import PaginationControls from "../components/PaginationControls";
import { API_BASE_URL } from "../config";
import "../styles/SATRequest.css";

const formatDate = (d, lang) => {
  if (!d) return "";
  const locale = lang === "tr" ? "tr-TR" : "en-US";
  return new Date(d).toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Sekme -> Excel export status parametresi
const EXPORT_STATUS = {
  pending: "pending_supervisor,pending_it,pending_manager,pending_hr",
  completed: "completed",
  closed: "rejected,cancelled",
};

const AdminSATRequestsPage = () => {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [supervisorMineIds, setSupervisorMineIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const initialTabChosen = useRef(false);
  const [deptOpen, setDeptOpen] = useState(false);
  const [deptRows, setDeptRows] = useState([]);
  const [deptLoading, setDeptLoading] = useState(false);
  const [deptError, setDeptError] = useState("");
  const [deptRange, setDeptRange] = useState({ from: "", to: "" });
  // "department" veya "team"
  const [deptGroupBy, setDeptGroupBy] = useState("department");

  const isIT = hasAnyRole(user, ["it", "admin"]);
  const isManager = hasAnyRole(user, ["company_manager", "admin"]);
  const isHr = hasAnyRole(user, ["hr", "admin"]);

  const isMine = useCallback(
    (r) =>
      (r.status === "pending_supervisor" && supervisorMineIds.has(r.id)) ||
      (r.status === "pending_it" && isIT) ||
      (r.status === "pending_manager" && isManager) ||
      (r.status === "pending_hr" && isHr),
    [supervisorMineIds, isIT, isManager, isHr]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [all, mine] = await Promise.all([
        satRequestService.listAll({}),
        satRequestService.listAll({ status: "pending_supervisor", supervisor_mine: "1" }),
      ]);
      setItems(Array.isArray(all) ? all : []);
      setSupervisorMineIds(new Set((Array.isArray(mine) ? mine : []).map((r) => r.id)));
    } catch (err) {
      setError(err.response?.data?.msg || err.message || t("common.genericError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const tabPredicates = useMemo(
    () => ({
      mine: isMine,
      pending: (r) => isPendingStatus(r.status),
      completed: (r) => r.status === "completed",
      closed: (r) => isClosedStatus(r.status),
      all: () => true,
    }),
    [isMine]
  );
  const tabCounts = useMemo(() => countBy(items, tabPredicates), [items, tabPredicates]);

  // Ilk yuklemede sirasi bende talep varsa o sekmeyle ac.
  useEffect(() => {
    if (loading || initialTabChosen.current) return;
    initialTabChosen.current = true;
    if (tabCounts.mine > 0) setTab("mine");
  }, [loading, tabCounts.mine]);

  const tabItems = useMemo(() => items.filter(tabPredicates[tab] || tabPredicates.all), [items, tabPredicates, tab]);
  const list = useSatListFilters(tabItems, {
    tabKey: tab,
    pageSizeKey: "pageSize.satAdmin",
    defaultSort: "newest",
  });
  const tabs = ["mine", "pending", "completed", "closed", "all"].map((key) => ({
    key,
    label: t(`satList.tabs.${key}`),
    count: tabCounts[key],
    attention: key === "mine",
  }));

  const exportHref = useMemo(() => {
    const q = new URLSearchParams();
    const status = list.filters.status || EXPORT_STATUS[tab];
    if (status) q.set("status", status);
    if (list.filters.from) q.set("from", list.filters.from);
    if (list.filters.to) q.set("to", list.filters.to);
    const qs = q.toString();
    return `${API_BASE_URL}/sat-requests/export.xlsx${qs ? `?${qs}` : ""}`;
  }, [tab, list.filters.status, list.filters.from, list.filters.to]);

  const loadDept = useCallback(
    async (groupBy = deptGroupBy) => {
      setDeptLoading(true);
      setDeptError("");
      try {
        const params = {};
        if (deptRange.from) params.from = deptRange.from;
        if (deptRange.to) params.to = deptRange.to;
        const data =
          groupBy === "team"
            ? await satRequestService.statsByTeam(params)
            : await satRequestService.statsByDepartment(params);
        setDeptRows(Array.isArray(data) ? data : []);
      } catch (err) {
        setDeptError(err.response?.data?.msg || err.message || t("common.genericError"));
      } finally {
        setDeptLoading(false);
      }
    },
    [deptRange, deptGroupBy, t]
  );

  const openDept = () => {
    setDeptOpen(true);
    loadDept(deptGroupBy);
  };

  const switchGroupBy = (g) => {
    setDeptGroupBy(g);
    loadDept(g);
  };

  return (
    <div className="sat-page">
      <div className="sat-page-header">
        <div>
          <h2>{t("nav.satReview")}</h2>
          <div className="subtitle">{t("satRequest.subtitle")}</div>
        </div>
        <div className="sat-header-actions">
          <button type="button" className="sat-btn" onClick={load} disabled={loading}>
            ↻ {t("satList.refresh")}
          </button>
          <button type="button" className="sat-btn" onClick={openDept}>
            {t("satRequest.departmentReport") || "Departman Raporu"}
          </button>
          <a className="sat-btn sat-btn--primary" href={exportHref}>
            {t("satRequest.exportXlsx") || "Excel'e aktar"}
          </a>
        </div>
      </div>

      {error && (
        <div className="sat-empty sat-empty--error" role="alert">
          {error}{" "}
          <button type="button" className="sat-btn" onClick={load}>
            {t("satList.refresh")}
          </button>
        </div>
      )}
      {loading && <div className="sat-empty">{t("common.loading")}</div>}

      {!loading && !error && items.length === 0 && (
        <EmptyState message={t("satRequest.empty")} />
      )}

      {!loading && items.length > 0 && (
        <>
          <TabBar tabs={tabs} value={tab} onChange={setTab} ariaLabel={t("nav.satReview")} />
          <SatFilterBar list={list} showRequester showStage />

          {list.filtered.length === 0 ? (
            <EmptyState message={t("satList.emptyFiltered")} />
          ) : (
            <>
              <table className="sat-table">
                <thead>
                  <tr>
                    <th>{t("satRequest.fields.code")}</th>
                    <th>{t("satRequest.fields.title")}</th>
                    <th>{t("satRequest.fields.createdBy")}</th>
                    <th>{t("satRequest.fields.category")}</th>
                    <th>{t("satRequest.fields.priority")}</th>
                    <th>{t("satRequest.fields.quantity")}</th>
                    <th>{t("satRequest.fields.totalPrice")}</th>
                    <th>{t("satRequest.fields.status")}</th>
                    <th>{t("satRequest.fields.createdAt")}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.paged.map((r) => {
                    const total = requestTotal(r);
                    const wait = isPendingStatus(r.status) ? waitingDays(r) : null;
                    const open = () => navigate(`/sat-requests/${r.id}`);
                    return (
                      <tr
                        key={r.id}
                        tabIndex={0}
                        onClick={open}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") open();
                        }}
                      >
                        <td className="sat-code">{r.request_code || "—"}</td>
                        <td>{r.title}</td>
                        <td>
                          {r.created_by_name} {r.created_by_surname}
                        </td>
                        <td>{t(`satRequest.category.${r.category}`)}</td>
                        <td>
                          <PriorityBadge priority={r.priority} />
                        </td>
                        <td>{r.quantity}</td>
                        <td>
                          {total ? `${total.toLocaleString(lang === "tr" ? "tr-TR" : "en-US")} TL` : "—"}
                        </td>
                        <td>
                          <StatusBadge status={r.status} />
                          {wait != null && (
                            <div className={`sat-waiting${wait >= 3 ? " late" : ""}`}>
                              {wait === 0
                                ? t("satList.waitingToday")
                                : `${wait} ${t("satList.waiting")}`}
                            </div>
                          )}
                        </td>
                        <td>{formatDate(r.created_at, lang)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <PaginationControls
                totalItems={list.filtered.length}
                page={list.page}
                pageSize={list.pageSize}
                onPageChange={list.setPage}
                onPageSizeChange={list.setPageSize}
              />
            </>
          )}
        </>
      )}

      {deptOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="sat-modal-backdrop"
          onClick={() => !deptLoading && setDeptOpen(false)}
        >
          <div className="sat-modal sat-modal--lg" onClick={(e) => e.stopPropagation()}>
            <div className="sat-modal__head">
              <h3>
                {deptGroupBy === "team"
                  ? t("satRequest.teamReport") || "Ekip Raporu"
                  : t("satRequest.departmentReport") || "Departman Raporu"}
              </h3>
              <button
                type="button"
                className="sat-modal__close"
                onClick={() => setDeptOpen(false)}
                aria-label={t("satList.dismiss")}
              >
                ×
              </button>
            </div>
            <div className="sat-dept-controls">
              <div role="tablist" aria-label={t("satRequest.groupBy") || "Grupla"} className="sat-dept-tabs">
                {[
                  { k: "department", l: t("satRequest.groupByDepartment") || "Departman" },
                  { k: "team", l: t("satRequest.groupByTeam") || "Ekip" },
                ].map((opt) => (
                  <button
                    key={opt.k}
                    type="button"
                    role="tab"
                    aria-selected={deptGroupBy === opt.k}
                    onClick={() => switchGroupBy(opt.k)}
                    className={`sat-btn${deptGroupBy === opt.k ? " sat-btn--primary" : ""}`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
              <label className="sat-dept-field">
                {t("satRequest.dateFrom") || "Başlangıç"}
                <input
                  type="date"
                  value={deptRange.from}
                  onChange={(e) => setDeptRange({ ...deptRange, from: e.target.value })}
                />
              </label>
              <label className="sat-dept-field">
                {t("satRequest.dateTo") || "Bitiş"}
                <input
                  type="date"
                  value={deptRange.to}
                  onChange={(e) => setDeptRange({ ...deptRange, to: e.target.value })}
                />
              </label>
              <button
                type="button"
                className="sat-btn sat-btn--primary"
                onClick={() => loadDept(deptGroupBy)}
                disabled={deptLoading}
              >
                {deptLoading ? t("common.loading") : t("satRequest.apply") || "Uygula"}
              </button>
            </div>
            {deptError && (
              <div className="sat-form-errors" role="alert">
                {deptError}
              </div>
            )}
            {!deptLoading && deptRows.length === 0 && !deptError && (
              <div className="sat-empty sat-dept-empty">
                {t("satRequest.empty")}
              </div>
            )}
            {!deptLoading && deptRows.length > 0 && (
              <table className="sat-table sat-dept-table">
                <thead>
                  <tr>
                    <th>
                      {deptGroupBy === "team"
                        ? t("satRequest.teamLabel") || "Ekip"
                        : t("satRequest.fields.department") || "Departman"}
                    </th>
                    <th>{t("satRequest.fields.total") || "Toplam"}</th>
                    <th>{t("satRequest.status.completed")}</th>
                    <th>{t("satRequest.status.rejected")}</th>
                    <th>{t("satReportUi.open")}</th>
                    <th>{t("satReportUi.plan")}</th>
                    <th>{t("satReportUi.actual")}</th>
                    <th>{t("satReportUi.diff")}</th>
                  </tr>
                </thead>
                <tbody>
                  {deptRows.map((r) => {
                    const diff = Number(r.diff || 0);
                    const diffColor = diff > 0 ? "#c33" : diff < 0 ? "#2e8b2e" : "#555";
                    const fmt = (n) =>
                      Number(n || 0).toLocaleString(lang === "tr" ? "tr-TR" : "en-US");
                    const label = deptGroupBy === "team" ? r.team : r.department;
                    return (
                      <tr key={label}>
                        <td>
                          <strong>{label}</strong>
                        </td>
                        <td>{r.total}</td>
                        <td>{r.completed}</td>
                        <td>{r.rejected}</td>
                        <td>{r.open_count}</td>
                        <td>{fmt(r.plan_total)}</td>
                        <td>{fmt(r.actual_total)}</td>
                        <td style={{ color: diffColor, fontWeight: 600 }}>
                          {diff > 0 ? "+" : ""}
                          {fmt(diff)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSATRequestsPage;
